/**
 * Admin 「외부 연동」 조회·변경 훅.
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 *
 * 조회는 staleTime 0(전역 5분을 끈다) — 다른 관리자가 키를 폐기했는지 같은 것이 바로 보여야 한다.
 * 변경은 성공하면 목록·상세를 다시 불러오고, 실패하면 서버 사유(`PartnerAdminError.message`)를
 * 토스트로 보여준다. 화면은 `mutateAsync`로 성공 여부를 받아 창을 닫거나 원문 표시 창을 연다.
 */
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ToastManager } from "components/organisms/CommonUI";
import {
  clearWebhookSecret,
  createPartner,
  generateWebhookSecret,
  getPartnerDetail,
  getPartnerMeta,
  issueApiKey,
  listPartners,
  registerRawApiKey,
  revealApiKey,
  revokeApiKey,
  setWebhookSecret,
  updateApiKey,
  updatePartner,
  type CreatePartnerBody,
  type IssueKeyBody,
  type PartnerDetail,
  type PartnerMeta,
  type PartnerSummary,
  type UpdateKeyBody,
  type UpdatePartnerBody,
} from "api/partnerAdminService";

export const partnerQueryKeys = {
  all: ["partnerAdmin"] as const,
  meta: ["partnerAdmin", "meta"] as const,
  list: ["partnerAdmin", "list"] as const,
  detail: (id: number) => ["partnerAdmin", "detail", id] as const,
};

const errorText = (err: unknown): string =>
  err instanceof Error && err.message ? err.message : "요청을 처리하지 못했습니다.";

const toastError = (err: unknown): void => {
  ToastManager.show({ content: errorText(err), duration: 4000 });
};
const toastDone = (content: string): void => {
  ToastManager.show({ content, duration: 2000 });
};

export const usePartnerMeta = () =>
  useQuery<PartnerMeta, Error>(partnerQueryKeys.meta, getPartnerMeta, { staleTime: 0 });

export const usePartnerList = () =>
  useQuery<PartnerSummary[], Error>(partnerQueryKeys.list, listPartners, { staleTime: 0 });

export const usePartnerDetail = (id: number) =>
  useQuery<PartnerDetail, Error>(partnerQueryKeys.detail(id), () => getPartnerDetail(id), {
    staleTime: 0,
    enabled: Number.isInteger(id) && id > 0,
  });

/** 파트너 등록 — 목록 화면에서 쓴다. */
export const useCreatePartner = () => {
  const qc = useQueryClient();
  return useMutation((body: CreatePartnerBody) => createPartner(body), {
    onSuccess: () => {
      qc.invalidateQueries(partnerQueryKeys.list);
      toastDone("파트너를 등록했습니다.");
    },
    onError: toastError,
  });
};

/** 상세 화면의 변경 전부. 성공하면 목록·해당 상세를 다시 불러온다. */
export const usePartnerMutations = (partnerId: number) => {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries(partnerQueryKeys.list);
    qc.invalidateQueries(partnerQueryKeys.detail(partnerId));
  };
  const handlers = (doneMessage?: string) => ({
    onSuccess: () => {
      refresh();
      if (doneMessage) toastDone(doneMessage);
    },
    onError: toastError,
  });

  return {
    update: useMutation((body: UpdatePartnerBody) => updatePartner(partnerId, body), handlers("저장했습니다.")),
    setActive: useMutation(
      (isActive: boolean) => updatePartner(partnerId, { isActive }),
      {
        onSuccess: (_data: PartnerSummary, isActive: boolean) => {
          refresh();
          toastDone(isActive ? "활성으로 바꿨습니다." : "비활성으로 바꿨습니다.");
        },
        onError: toastError,
      }
    ),
    // 발급·생성은 원문 표시 창이 결과를 알리므로 완료 토스트를 띄우지 않는다.
    issueKey: useMutation((body: IssueKeyBody) => issueApiKey(partnerId, body), handlers()),
    updateKey: useMutation(
      ({ keyId, body }: { keyId: number; body: UpdateKeyBody }) => updateApiKey(partnerId, keyId, body),
      handlers("키를 저장했습니다.")
    ),
    revokeKey: useMutation((keyId: number) => revokeApiKey(partnerId, keyId), handlers("키를 폐기했습니다.")),
    // [원문 복사] — 성공 토스트·복사는 호출부(copyRevealedKey)가 하고, 여기서는 상세를 다시 불러온다.
    // 성공·실패 모두 재조회한다(AC-F12): 새 이력 행이 보이고, 다른 관리자가 그새 폐기했으면 반영된다.
    // 실패 사유(409·복호화 실패 등)는 toastError가 서버 message 그대로 보여준다.
    revealKey: useMutation((keyId: number) => revealApiKey(partnerId, keyId), {
      onSettled: () => qc.invalidateQueries(partnerQueryKeys.detail(partnerId)),
      onError: toastError,
    }),
    registerRawKey: useMutation(
      ({ keyId, rawKey }: { keyId: number; rawKey: string }) => registerRawApiKey(partnerId, keyId, rawKey),
      handlers("원문을 등록했습니다.")
    ),
    setSecret: useMutation(
      (webhookSecret: string) => setWebhookSecret(partnerId, webhookSecret),
      handlers("서명 키를 저장했습니다.")
    ),
    generateSecret: useMutation(
      (provision: boolean | undefined) =>
        generateWebhookSecret(partnerId, provision === undefined ? {} : { provision }),
      handlers()
    ),
    clearSecret: useMutation(() => clearWebhookSecret(partnerId), handlers("서명 키 DB 값을 지웠습니다.")),
  };
};
